import PageLayout from '../components/PageLayout'
import PageHero from '../components/PageHero'
import ProseContent from '../components/ProseContent'

const sections = [
  {
    heading: 'Agreement to these terms',
    body: (
      <>
        <p>
          These Terms of Service (the "Terms") are a binding agreement between you and Oak Platforms Inc. ("Oak"). By creating an account, accessing the Oak platform, or using any Oak-powered marketplace, you agree to these Terms.
        </p>
        <p>
          If you are agreeing on behalf of a company or other legal entity, you represent that you have authority to bind that entity. In that case, "you" refers to that entity.
        </p>
      </>
    ),
  },
  {
    heading: 'Who we are',
    body: (
      <>
        <p>
          Oak provides white-label marketplace infrastructure. Moderators use Oak to launch, brand, and operate a marketplace for their community. Oak handles payments, payouts, messaging, listings, analytics, and agentic discovery; the moderator defines the community rules and keeps the relationship with their members.
        </p>
      </>
    ),
  },
  {
    heading: 'Your account',
    body: (
      <>
        <p>
          You must provide accurate information when you sign up and keep that information current. You are responsible for maintaining the security of your credentials and for all activity under your account. Notify us immediately at <a href="mailto:security@oakplatforms.com" className="text-oak-black underline underline-offset-2">security@oakplatforms.com</a> if you suspect unauthorized use.
        </p>
      </>
    ),
  },
  {
    heading: 'Acceptable use',
    body: (
      <>
        <p>You agree not to use Oak to:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>List or sell anything that is illegal in the jurisdictions where your marketplace operates.</li>
          <li>Infringe on intellectual property, privacy, or publicity rights.</li>
          <li>Harass, threaten, or target members or Oak staff.</li>
          <li>Attempt to bypass authentication, rate limits, fraud controls, or platform security.</li>
          <li>Resell, sublicense, or reverse-engineer the Oak platform outside the terms of our SDK agreements.</li>
        </ul>
        <p>
          We may suspend or terminate accounts that violate these rules, with notice where it is practical to provide it.
        </p>
      </>
    ),
  },
  {
    heading: 'Moderator responsibilities',
    body: (
      <>
        <p>
          If you operate an Oak-powered marketplace, you are responsible for the rules of your community, the legitimacy of the activity that happens there, and your obligations to members, sellers, and applicable regulators. This includes, where relevant, tax and sales reporting, consumer-protection compliance, and the enforcement of community guidelines.
        </p>
        <p>
          Oak gives you tooling. You give it judgment.
        </p>
      </>
    ),
  },
  {
    heading: 'Payments and fees',
    body: (
      <>
        <p>
          Oak processes payments through Stripe and Stripe Connect. By using Oak, you also agree to the applicable Stripe Services Agreement and Connected Account Agreement. Platform fees, seller fees, and payout schedules are documented on the Pricing page and in your operator agreement.
        </p>
        <p>
          Refunds, chargebacks, and disputes are governed by Stripe's rules and by the refund policy your marketplace publishes to its members.
        </p>
      </>
    ),
  },
  {
    heading: 'Intellectual property',
    body: (
      <>
        <p>
          <strong>Your content.</strong> You retain ownership of the listings, messages, branding, and other content you create on Oak. You grant us a worldwide, non-exclusive license to host, display, and process that content to the extent necessary to provide the platform.
        </p>
        <p>
          <strong>Our platform.</strong> Oak retains all rights in the platform itself — code, designs, trademarks, and documentation. Nothing in these Terms transfers those rights to you.
        </p>
      </>
    ),
  },
  {
    heading: 'Third-party services',
    body: (
      <>
        <p>
          Oak integrates with third-party services including Stripe, Apple, and Google. Your use of those services is subject to their own terms. Oak is not responsible for outages, changes, or decisions made by those providers outside our reasonable control.
        </p>
      </>
    ),
  },
  {
    heading: 'Disclaimers',
    body: (
      <>
        <p>
          Oak is provided on an "as is" and "as available" basis. We do our best to keep the platform reliable, but we do not warrant that it will be uninterrupted or error-free. To the maximum extent permitted by law, we disclaim all implied warranties, including merchantability and fitness for a particular purpose.
        </p>
      </>
    ),
  },
  {
    heading: 'Limitation of liability',
    body: (
      <>
        <p>
          To the maximum extent permitted by law, Oak's total liability arising out of or relating to these Terms will not exceed the greater of (a) amounts paid to Oak by you in the twelve months before the claim arose and (b) one thousand US dollars (US$1,000). Neither party will be liable for indirect, incidental, special, consequential, or punitive damages.
        </p>
      </>
    ),
  },
  {
    heading: 'Termination',
    body: (
      <>
        <p>
          You may close your account at any time. We may suspend or terminate your access if you violate these Terms, if we are required to do so by law, or if continuing to provide the platform would present material risk to other users or to Oak.
        </p>
        <p>
          On termination, we will give you a reasonable opportunity to export your data. Sections that should reasonably survive — including intellectual property, disclaimers, limitation of liability, and dispute resolution — survive termination.
        </p>
      </>
    ),
  },
  {
    heading: 'Governing law and disputes',
    body: (
      <>
        <p>
          These Terms are governed by the laws of the State of New York, without regard to conflict-of-law principles. Disputes will be resolved in the state or federal courts located in New York County, New York, and the parties consent to the exclusive jurisdiction of those courts.
        </p>
      </>
    ),
  },
  {
    heading: 'Changes to these terms',
    body: (
      <>
        <p>
          We may update these Terms from time to time. If the changes are material, we will provide at least 30 days' notice before they take effect. Continuing to use Oak after changes take effect means you accept the updated Terms.
        </p>
      </>
    ),
  },
  {
    heading: 'Contact',
    body: (
      <>
        <p>
          Legal notices should be sent to <a href="mailto:legal@oakplatforms.com" className="text-oak-black underline underline-offset-2">legal@oakplatforms.com</a> and to Oak Platforms Inc., 228 Park Ave S, PMB 84351, New York, NY 10003, United States.
        </p>
      </>
    ),
  },
]

export default function TermsPage() {
  return (
    <PageLayout>
      <PageHero
        eyebrow="Terms of Service"
        title="The ground rules for using Oak."
        description="The short version: use Oak in good faith, follow the rules of the jurisdictions you operate in, and we will do right by you."
      />
      <ProseContent
        updated="April 1, 2026"
        intro={
          <p>
            These Terms govern your use of the Oak platform, our website, and any marketplace built with Oak. Please read them carefully.
          </p>
        }
        sections={sections}
      />
    </PageLayout>
  )
}
